import { userRequestedSearch } from '@web-platform/shared-banana-redux/src/actions/searchControls';
import { getContextRequestSnapshot } from '@web-platform/shared-banana-utils-frontend/src/helpers/contextPromise';
import { appInit } from '@web-platform/shared-universal-redux/src/actions/app';
import {
  searchParamsInit,
  searchParamsSaved,
} from '@web-platform/shared-universal-redux/src/actions/user';
import { transformContextUserSearch } from '@web-platform/shared-universal-translations';
import { getVirtualReferrerFromLocationState } from '@web-platform/shared-universal-utils-frontend/src/helpers/getLocationState';
import { logWarn } from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import isEqual from 'lodash/isEqual';
import pick from 'lodash/pick';
import { GLOBAL } from 'saddlebag-browser';
import observer from 'saddlebag-observer';

import type { ContextBody } from '@web-platform/shared-universal-types/src/context';
import type { Action } from '@web-platform/shared-universal-types/src/redux/banana/actions/actions-banana-types';
import type { State } from '@web-platform/shared-universal-types/src/redux/banana/state/state-banana-types';
import type { Location } from 'react-router-dom';
import type { Store } from 'redux';

const component = 'hydrate-context-search-params-diff';

// webappParams.ts sets legs on windowSearchParams as a backwards-compatibility alignment
// with Acorn (see apps/banana-server/app/routes/banana/webappParams.ts), but Banana's
// context API call doesn't return this field on flightSearch, so it's excluded here.
//
// raw_origin_id, raw_destination_id, originId, originIata, destinationId, nearestAirport:
// Banana's context API call passes through context.flightSearch in full, so these fields
// are present there, but they have no consumers and aren't carried over when building
// windowSearchParams.
const IGNORED_DIVERGING_FIELDS = new Set([
  'legs',
  'raw_origin_id',
  'raw_destination_id',
  'originId',
  'originIata',
  'destinationId',
  'nearestAirport',
]);

const getDivergingFields = (
  flightSearch: Record<string, unknown>,
  windowSearchParams: Record<string, unknown>,
) => {
  const keys = new Set([...Object.keys(flightSearch), ...Object.keys(windowSearchParams)]);
  const divergingFields: string[] = [];

  keys.forEach((key) => {
    if (
      !IGNORED_DIVERGING_FIELDS.has(key) &&
      !isEqual(flightSearch[key], windowSearchParams[key])
    ) {
      divergingFields.push(key);
    }
  });

  return divergingFields;
};

export const hydrateContext = async (
  store: Store<State, Action>,
  context: ContextBody,
  historyLocation: Location,
) => {
  const { flightSearch, origin: nearestOrigin } = context;

  store.dispatch(
    appInit({
      origin: nearestOrigin,
      nearestOrigin,
    }),
  );

  const virtualReferrer = getVirtualReferrerFromLocationState(historyLocation);

  // See pull/2052 for virtualReferrer details, along with virtual-referrer.md
  observer.publish('trackolding-spa-acquisition', {
    virtualReferrer,
  });

  if (flightSearch) {
    const windowSearchParams = GLOBAL.getWindow().__internal?.searchParams;
    if (windowSearchParams) {
      const divergingFields = getDivergingFields(flightSearch, windowSearchParams);
      if (divergingFields.length > 0) {
        // Will delete this log when we clean the flightSearch from the context API call
        logWarn({
          component,
          message: 'flightSearch context differs from window.__internal.searchParams',
          divergingFields: divergingFields.join(', '),
          flightSearchValues: JSON.stringify(pick(flightSearch, divergingFields)),
          windowSearchParamsValues: JSON.stringify(pick(windowSearchParams, divergingFields)),
          currentUrl: GLOBAL.getWindow().location.href,
          contextRequestUrl: getContextRequestSnapshot(),
        });
      }
    }

    const searchParams = transformContextUserSearch(flightSearch);
    store.dispatch(searchParamsInit(searchParams));
    store.dispatch(searchParamsSaved(searchParams));
    store.dispatch(userRequestedSearch(true));
  }
};
